import {Box, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';
import {match, Pattern} from 'ts-pattern';

import type {AdvertisementPreviewPlatform} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER, SkeletonLoadingBox} from '@dms/shared';

import type {RequiredTestIdProps} from 'shared/web';

import {convertPlatformDescriptionToHtml} from '../../../utils/convertPlatformDescriptionToHtml';
import {sanitizeAdvertisementDescription} from '../../../utils/sanitizeAdvertisementDescription';

const HTML_TAG_PATTERN = /<\/?[a-z][^>]*>/i;
const DESCRIPTION_SKELETON_LINE_COUNT = 7;
type PreviewDescriptionFormat = AdvertisementPreviewPlatform['description']['format'];

export function AdvertisementPreviewDescription(
  props: {
    value: string | null;
    format: PreviewDescriptionFormat;
    isDefaultLanguage: boolean;
    isLoading: boolean;
  } & RequiredTestIdProps
) {
  return (
    <Box flexGrow={1} flexShrink={1} flexBasis="auto">
      <VStack spacing={2}>
        {props.isLoading ? (
          <VStack spacing={2} align="stretch" width="100%" data-testid={props['data-testid']}>
            {Array.from({length: DESCRIPTION_SKELETON_LINE_COUNT}, (_, index) => (
              <Box
                key={index}
                width={index === DESCRIPTION_SKELETON_LINE_COUNT - 1 ? '70%' : '100%'}
              >
                <SkeletonLoadingBox isLoading height={4} />
              </Box>
            ))}
          </VStack>
        ) : (
          <Description
            value={props.value}
            format={props.format}
            isDefaultLanguage={props.isDefaultLanguage}
          />
        )}
      </VStack>
    </Box>
  );
}

function Description(props: {
  value: string | null;
  format: PreviewDescriptionFormat;
  isDefaultLanguage: boolean;
}) {
  return match({value: props.value, isDefaultLanguage: props.isDefaultLanguage})
    .with({value: null, isDefaultLanguage: true}, () => (
      <Text color="secondary">{EMPTY_PLACEHOLDER}</Text>
    ))
    .with({value: null, isDefaultLanguage: false}, () => (
      <Text color="secondary">{i18n.t('page.advertisement.preview.notTranslated')}</Text>
    ))
    .with({value: Pattern.string}, ({value}) => {
      const format = props.format;
      if (format === 'plain_text' || (format === 'html' && !HTML_TAG_PATTERN.test(value))) {
        return (
          <Text size="small" preWrap overflowWrap="anywhere">
            {value}
          </Text>
        );
      }

      const html = format === 'html' ? value : convertPlatformDescriptionToHtml(value, format);
      const sanitizedHtml = sanitizeAdvertisementDescription(html);
      return (
        <Text size="small" overflowWrap="anywhere">
          {/* eslint-disable-next-line react/no-danger -- HTML sanitized by strict sanitizeAdvertisementDescription allowlist */}
          <SanitizedDescription dangerouslySetInnerHTML={{__html: sanitizedHtml}} />
        </Text>
      );
    })
    .exhaustive();
}

// eslint-disable-next-line eag/no-css-property -- Reset default margins of sanitized HTML paragraphs
const SanitizedDescription = styled.div`
  line-height: 1.75;

  li + li {
    margin-top: ${({theme}) => theme.getSize(1)};
  }

  p:first-child {
    margin-top: 0;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;
